import { useAccount } from "@qw/auth";
import { cn, Icon, InsuredPin, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import { Link, useParams } from "react-router";
import { useBook } from "@/providers/use-book";
import { usePage } from "@/providers/use-page";
import { useBookRootPath } from "./book-navigation";
import { BookRow } from "./book-row";
import { BookRowGutter, type BookRowVariant } from "./book-row-gutter";
import { RenewalStatusPicker } from "./renewal-status-picker";
import type { ItemWithInsured } from "./use-pinned-insureds";

type InsuredLinkVariant = BookRowVariant;

export const InsuredLink = <T extends { id?: string }>({
  item,
  variant = "default",
  className,
}: React.ComponentProps<"div"> & {
  item: ItemWithInsured<T>;
  variant?: InsuredLinkVariant;
}) => {
  const { insuredId } = useParams();
  const { rootPath } = usePage();
  const bookRootPath = useBookRootPath();
  const { onSelectInsured } = useBook();
  const { user } = useAccount();
  const { insured, renewalStatus, brokerId } = item;
  const opportunityId = (item as T).id;
  // A row is here because the viewer owns it or collaborates on it (the server scopes the
  // query to owner-or-collaborator). When the owner is someone else, the viewer collaborates.
  const isCollaborator = Boolean(brokerId && brokerId !== user.id);
  const insuredPath = `${bookRootPath ?? rootPath}/${insured.id}`;

  return (
    <BookRow
      className={cn(
        variant === "renewal" ? "grid-cols-[1fr_auto_--spacing(8)]" : "grid-cols-[1fr_--spacing(8)]",
        className
      )}
    >
      <Link
        to={insuredPath}
        aria-current={insuredId === insured.id}
        className="group gap-3 grid grid-cols-[--spacing(18)_1fr] items-center pl-1 text-xs"
        onClick={() => onSelectInsured?.(insured)}
      >
        <BookRowGutter item={item} variant={variant} />
        <span className="flex gap-1.5 items-center min-w-0">
          <span className="truncate">{insured.name}</span>
          {isCollaborator && (
            <Tooltip delayDuration={500}>
              <TooltipTrigger asChild>
                <span className="flex-none inline-flex text-muted-foreground group-aria-current:text-primary">
                  <Icon icon="group" className="text-2xs" />
                </span>
              </TooltipTrigger>
              <TooltipContent>You collaborate on this plan.</TooltipContent>
            </Tooltip>
          )}
        </span>
      </Link>
      {variant === "renewal" && opportunityId && (
        <RenewalStatusPicker opportunityId={opportunityId} currentStatus={renewalStatus} />
      )}
      <InsuredPin
        id={insured.id}
        size="2xs"
        className="bg-transparent! pointer-events-none size-8 text-2xs opacity-0 transition-opacity group-hover/book-row:pointer-events-auto group-hover/book-row:opacity-100 group-focus-within/book-row:pointer-events-auto group-focus-within/book-row:opacity-100 data-[pinned=true]:pointer-events-auto data-[pinned=true]:opacity-100 any-pointer-coarse:pointer-events-auto any-pointer-coarse:opacity-100"
      />
    </BookRow>
  );
};
